import { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { useParams } from 'react-router-dom';
import { enterGate, validateIAmsterdamCard, GateError } from '../services/auth';
import { trackGateSubmitted } from '../mixpanel';

function maskIdSuffix(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  const s = String(value);
  if (s.length <= 4) return '****';
  return `****${s.slice(-4)}`;
}

const IAC_ERROR_MESSAGES: Record<string, string> = {
  already_used: 'iamsterdam-error-used',
  expired: 'iamsterdam-error-expired',
  press_card: 'iamsterdam-error-unsuitable',
  unsuitable: 'iamsterdam-error-unsuitable',
  unknown: 'iamsterdam-error-unknown',
  missing_card_id: 'iamsterdam-error-unknown',
  invalid_card_id: 'iamsterdam-error-unknown',
  rate_limited: 'iamsterdam-error-unknown',
  // NOT the timeout copy: auth_error means our IAC token is rejected — a
  // config failure. "Try again" would send visitors into a retry loop.
  iac_auth_error: 'iamsterdam-error-unknown',
  iac_api_error: 'iamsterdam-error-unknown',
  iac_timeout: 'iamsterdam-error-timeout',
  internal_error: 'iamsterdam-error-unknown',
};

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
const THIRTY_DAYS_SEC = 30 * 24 * 60 * 60;
const COOKIE_PREFIX = 'xsite_gate_';

function extractTimestamp(token: string): number {
  const firstPart = token.split('.')[0];

  const direct = parseInt(firstPart, 10);
  if (!isNaN(direct) && String(direct) === firstPart) return direct;

  try {
    const decoded = atob(firstPart);
    const colonIdx = decoded.lastIndexOf(':');
    if (colonIdx !== -1) {
      return parseInt(decoded.substring(colonIdx + 1), 10);
    }
  } catch { /* not valid base64 */ }

  return NaN;
}

const hasSessionToken = (token: string | null): boolean => {
  if (typeof token !== 'string' || token.length < 10 || !token.includes('.')) return false;
  const timestamp = extractTimestamp(token);
  if (isNaN(timestamp)) return false;
  return Date.now() - timestamp < THIRTY_DAYS_MS;
};

function writeSessionCookie(slug: string, token: string): void {
  try {
    const name = `${COOKIE_PREFIX}${slug}`;
    document.cookie = `${name}=${encodeURIComponent(token)};path=/;max-age=${THIRTY_DAYS_SEC};SameSite=Lax;Secure`;
  } catch { /* cookie write failed */ }
}

function readSessionCookie(slug: string): string | null {
  try {
    const prefix = `${COOKIE_PREFIX}${slug}=`;
    for (const part of document.cookie.split(';')) {
      const trimmed = part.trim();
      if (trimmed.startsWith(prefix)) {
        return decodeURIComponent(trimmed.substring(prefix.length));
      }
    }
  } catch { /* cookie read failed */ }
  return null;
}

function readSession(slug: string): string | null {
  const lsKey = `xsite_${slug}_session`;
  try {
    const stored = window.localStorage.getItem(lsKey);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (hasSessionToken(parsed)) {
        writeSessionCookie(slug, parsed);
        return parsed;
      }
    }
  } catch { /* localStorage unavailable or corrupted */ }

  const cookieToken = readSessionCookie(slug);
  if (hasSessionToken(cookieToken)) {
    try {
      window.localStorage.setItem(lsKey, JSON.stringify(cookieToken));
    } catch { /* restore to localStorage failed — cookie still valid */ }
    return cookieToken;
  }

  return null;
}

function writeSession(slug: string, token: string | null): void {
  const lsKey = `xsite_${slug}_session`;
  try {
    window.localStorage.setItem(lsKey, JSON.stringify(token));
  } catch { /* localStorage write failed — cookie serves as fallback */ }
  if (token) {
    writeSessionCookie(slug, token);
  }
}

export interface IAmsterdamLoginResult {
  success: boolean;
  reason?: string;
  iacStatus?: number;
  iacType?: string | number;
  iacMessage?: string;
  tagIdMasked?: string;
}

export interface AuthContextValue {
  isAuthenticated: boolean;
  login: (code: string) => Promise<boolean>;
  loginWithIAmsterdam: (cardId: string) => Promise<IAmsterdamLoginResult>;
  error: string | null;
  clearError: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { slug } = useParams<{ slug: string }>();

  const [sessionToken, setSessionTokenState] = useState<string | null>(
    () => readSession(slug || '')
  );
  const legacyChecked = useRef(false);

  const setSessionToken = useCallback((token: string | null) => {
    writeSession(slug || '', token);
    setSessionTokenState(token);
  }, [slug]);

  useEffect(() => {
    if (legacyChecked.current || hasSessionToken(sessionToken)) return;
    legacyChecked.current = true;
    const legacyKey = `xsite_${slug}_isAuthenticated`;
    try {
      const legacy = window.localStorage.getItem(legacyKey);
      if (legacy === 'true') {
        window.localStorage.removeItem(legacyKey);
        setSessionToken(`${Date.now()}.${crypto.randomUUID()}`);
      }
    } catch { /* localStorage unavailable */ }
  }, [sessionToken, slug, setSessionToken]);

  const isAuthenticated = hasSessionToken(sessionToken);

  const login = async (code: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await enterGate(code);
      const token = result.sessionToken || `${Date.now()}.${crypto.randomUUID()}`;
      setSessionToken(token);
      trackGateSubmitted(code);
      return true;
    } catch (err) {
      if (err instanceof GateError) {
        setError(err.reason);
      } else {
        Sentry.captureException(err);
        setError('unknown');
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithIAmsterdam = async (cardId: string): Promise<IAmsterdamLoginResult> => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await validateIAmsterdamCard(cardId);

      if (result.valid) {
        setSessionToken(`${Date.now()}.${crypto.randomUUID()}`);
        return { success: true };
      }

      const errorKey = IAC_ERROR_MESSAGES[result.message] ?? 'iamsterdam-error-unknown';
      setError(errorKey);
      const iacMessage =
        result.iacMessage != null && result.iacMessage !== ''
          ? String(result.iacMessage)
          : undefined;
      return {
        success: false,
        reason: result.message,
        iacStatus: result.iacStatus,
        iacType: result.iacType,
        iacMessage,
        tagIdMasked: maskIdSuffix(result.tagId),
      };
    } catch (e) {
      Sentry.captureException(e);
      // The service layer propagates the backend's failure reason as the
      // Error message (iac_timeout / iac_auth_error / iac_api_error / ...).
      // Map known reasons to their i18n key; anything else is 'iac_error'.
      const message = e instanceof Error ? e.message : '';
      const known = Object.prototype.hasOwnProperty.call(IAC_ERROR_MESSAGES, message);
      setError(known ? IAC_ERROR_MESSAGES[message] : 'iamsterdam-error-unknown');
      return { success: false, reason: known ? message : 'iac_error' };
    } finally {
      setIsLoading(false);
    }
  };

  const clearError = (): void => setError(null);

  const value: AuthContextValue = {
    isAuthenticated,
    login,
    loginWithIAmsterdam,
    error,
    clearError,
    isLoading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
